/**
 * Owner: Masa Bayu
 * File: spsu-extractor.ts
 * Date:Time: 2026-10-24 00:00
 * Description: Raw Eyes/pdf.js → SPSUPage · Pass 3 gugur hanya atas bukti liputan/kandungan (§438 R-2) · carry tip linePaint (R11.4).
 */

import type {
  AssetLibrary,
  BBox,
  FaceLinePaint,
  ImageElement,
  SPSUElement,
  SPSUPage,
  TextElement,
  TextStyle,
} from './spsu-types';
import { isGhostBodyLinePlate } from '../render/spsu-ocr-line-plates-core';
import {
  adjudicatePlate,
  describePlateVerdict,
} from '../paint/spsu-plate-duplicate-evidence';

// =============================================================================
// 1. RAW INPUT (aligned to Eyes / pdf.js measured fields)
// =============================================================================

/** One live text run as delivered by Eyes / pdf.js — no invented paint. */
export type RawTextBlock = {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly text: string;
  /** CAPTURED font name · empty only when provider omitted it. */
  readonly fontName: string;
  /** CAPTURED size in page units · null when provider omitted it. */
  readonly fontSize: number | null;
  /** CAPTURED weight · null when provider omitted it (do not invent bold). */
  readonly fontWeight: number | null;
  /**
   * CAPTURED color (#RRGGBB / rgba) · null when NOT_AVAILABLE_FROM_PROVIDER.
   * Mapper does not invent black.
   */
  readonly color: string | null;
  /** Tip linePaint carry · R11.4 · omit when absent (no invent). */
  readonly linePaint?: FaceLinePaint;
};

export type RawImageBlock = {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly assetId: string;
  readonly layer?: number;
  readonly objectFit?: ImageElement['objectFit'];
};

export type RawPageData = {
  readonly pageNumber: number;
  readonly pageWidth: number;
  readonly pageHeight: number;
  /** PDF user space = pt · Face may later scale to CSS px. */
  readonly unit?: BBox['unit'];
  readonly textBlocks: readonly RawTextBlock[];
  readonly imageBlocks: readonly RawImageBlock[];
};

// =============================================================================
// 2. VALIDATE & MAP
// =============================================================================

/**
 * Size must be positive and finite.
 * Slight negative x/y is allowed — real PDF CTM bleed (P02 paper x≈−0.75).
 */
function validateBBox(bbox: BBox): void {
  if (
    !Number.isFinite(bbox.x) ||
    !Number.isFinite(bbox.y) ||
    !Number.isFinite(bbox.width) ||
    !Number.isFinite(bbox.height)
  ) {
    throw new Error(`Invalid BBox (non-finite): ${JSON.stringify(bbox)}`);
  }
  if (bbox.width <= 0 || bbox.height <= 0) {
    throw new Error(`Invalid BBox size: ${JSON.stringify(bbox)}`);
  }
}

/** Fraction of image area covered by text box — softmask glyph duplicates live text. */
export function imageTextOverlapRatio(image: BBox, text: BBox): number {
  const left = Math.max(image.x, text.x);
  const top = Math.max(image.y, text.y);
  const right = Math.min(image.x + image.width, text.x + text.width);
  const bottom = Math.min(image.y + image.height, text.y + text.height);
  const width = right - left;
  const height = bottom - top;
  if (width <= 0 || height <= 0) return 0;
  return (width * height) / Math.max(1, image.width * image.height);
}

const TEXT_SOFTMASK_OVERLAP = 0.2;

function unionBBox(a: BBox, b: BBox): BBox {
  const x0 = Math.min(a.x, b.x);
  const y0 = Math.min(a.y, b.y);
  const x1 = Math.max(a.x + a.width, b.x + b.width);
  const y1 = Math.max(a.y + a.height, b.y + b.height);
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0, unit: a.unit };
}

/**
 * Softmask wordmarks vs live text (U-FID-09 Class A · U-FID-10):
 * - light / unknown → drop plate · live text paints
 * - dark + text overlap → keep plate (measured glyph visual, e.g. citation `{ }`) · live text unpainted
 * - G1 TextElement.bbox = provider/source geometry · never overwritten here
 * - G2 TextElement.visualFootprint = softmask∪text · written here only
 *
 * Matching uses original G1 bboxes only (two-phase). Sequential match-after-union
 * on G1 would let an early light softmask inflate the first body line until it
 * steals later dark plates (citation Image114 → Surah) from their true text.
 *
 * Writer seed for accumulating G2 uses prior visualFootprint or G1 bbox —
 * that is write-path construction only · not a read-path fallback for consumers.
 */
function expandVisualFootprint(text: TextElement, plate: BBox): BBox {
  const seed = text.visualFootprint ?? text.bbox;
  return unionBBox(seed, plate);
}

export function reconcileTextAndSoftmasks(
  images: readonly ImageElement[],
  texts: readonly TextElement[],
  assets?: AssetLibrary,
  /** §438 R-2 · saluran rekod kes belum terbukti · optional · pemanggil lama tak berubah. */
  onNotProven?: (imageId: string, verdict: string) => void,
): { readonly images: ImageElement[]; readonly texts: TextElement[] } {
  if (texts.length === 0) return { images: [...images], texts: [] };
  const originBBoxes = texts.map((text) => ({ ...text.bbox }));
  const pairs: { readonly imageIndex: number; readonly textIndex: number }[] = [];
  for (let imageIndex = 0; imageIndex < images.length; imageIndex += 1) {
    const image = images[imageIndex]!;
    let bestText = -1;
    let bestRatio = 0;
    for (let textIndex = 0; textIndex < originBBoxes.length; textIndex += 1) {
      const ratio = imageTextOverlapRatio(image.bbox, originBBoxes[textIndex]!);
      if (ratio < TEXT_SOFTMASK_OVERLAP) continue;
      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestText = textIndex;
      }
    }
    if (bestText >= 0) pairs.push({ imageIndex, textIndex: bestText });
  }

  const nextTexts: TextElement[] = texts.map((text) => ({
    ...text,
    bbox: { ...text.bbox },
    style: { ...text.style },
    ...(text.visualFootprint
      ? { visualFootprint: { ...text.visualFootprint } }
      : {}),
  }));
  const dropImage = new Set<number>();
  // Text indexes claimed by a dark glyph plate · light must not stamp #FFFFFF over them.
  const darkClaimedText = new Set<number>();
  // Plate indexes yang Pass 1 miliki sebagai cat kukuh · teksnya sudah 'transparent'.
  // Pass 3 tidak boleh gugurkan plate ini · jika ya, run hilang semua paint (teks putih lut + plat gone).
  const pass1OwnedPlate = new Set<number>();
  // Pass 1 · dark plates first (image order alone can let light overwrite after).
  for (const { imageIndex, textIndex } of pairs) {
    const image = images[imageIndex]!;
    const tone = assets?.[image.assetId]?.paintTone;
    if (tone !== 'dark') continue;
    const text = nextTexts[textIndex]!;
    darkClaimedText.add(textIndex);
    pass1OwnedPlate.add(imageIndex);
    nextTexts[textIndex] = {
      ...text,
      visualFootprint: expandVisualFootprint(text, image.bbox),
      style: { ...text.style, color: 'transparent' },
    };
  }
  // Pass 2 · light / unknown / décor-sized plates.
  for (const { imageIndex, textIndex } of pairs) {
    const image = images[imageIndex]!;
    const text = nextTexts[textIndex]!;
    const tone = assets?.[image.assetId]?.paintTone;
    if (tone === 'dark') continue;
    // Cream / ivy / gold plaque ≫ text run · not a glyph softmask. Keep décor.
    // Chroma/gray art may only be ~3–4× a tall body paragraph (P18 cream) · light
    // line plates stay ~3× a single line and must still drop (Surah inflate case).
    const imageArea = Math.max(1, image.bbox.width * image.bbox.height);
    const textArea = Math.max(1, text.bbox.width * text.bbox.height);
    const decorTone = tone === 'chroma' || tone === 'gray';
    if (imageArea > textArea * (decorTone ? 2 : 4)) {
      continue;
    }
    dropImage.add(imageIndex);
    // Dark plate already owns this run · drop the light duplicate only.
    if (darkClaimedText.has(textIndex) || text.style.color.trim() === 'transparent') {
      nextTexts[textIndex] = {
        ...text,
        visualFootprint: expandVisualFootprint(text, image.bbox),
      };
      continue;
    }
    // Light softmask dropped · do not invent white. Empty → black body (renderer).
    // Title white paint must come from Eyes or a kept plate.
    const stampedColor = text.style.color.trim().length > 0 ? text.style.color : '';
    nextTexts[textIndex] = {
      ...text,
      visualFootprint: expandVisualFootprint(text, image.bbox),
      style: { ...text.style, color: stampedColor },
    };
  }
  // Pass 3 · §438 R-2 · plate garisan yang belum digugurkan.
  // paintTone + geometri HANYA menentukan kelayakan calon · pengguguran perlu BUKTI:
  // liputan ≥95% oleh kotak teks hidup yang mencetak · pertindihan separa = NOT PROVEN (kekalkan).
  for (let imageIndex = 0; imageIndex < images.length; imageIndex += 1) {
    if (dropImage.has(imageIndex)) continue;
    // Pass 1 sudah anugerahkan plate ini sebagai pemegang paint · teksnya transparent.
    if (pass1OwnedPlate.has(imageIndex)) continue;
    const image = images[imageIndex]!;
    const tone = assets?.[image.assetId]?.paintTone;
    if (!isGhostBodyLinePlate(image, tone)) continue;
    const verdict = adjudicatePlate({ plate: image.bbox, liveTexts: nextTexts });
    if (verdict.kind === 'duplicate') {
      dropImage.add(imageIndex);
      continue;
    }
    // unique / uncertain · plate kekalkan · uncertain = bukti tidak cukup → NOT PROVEN.
    if (verdict.kind === 'uncertain') {
      onNotProven?.(image.id, describePlateVerdict(verdict));
    }
  }

  const keptImages = images.filter((_, index) => !dropImage.has(index));
  return { images: keptImages, texts: nextTexts };
}

/** @deprecated prefer reconcileTextAndSoftmasks — kept for Face filter of legacy pages. */
export function dropTextSoftmaskDuplicates(
  images: readonly ImageElement[],
  texts: readonly TextElement[],
): ImageElement[] {
  return reconcileTextAndSoftmasks(images, texts).images;
}

function mapTextStyle(raw: RawTextBlock): TextStyle {
  if (raw.fontName.trim().length === 0) {
    throw new Error(`Text ${raw.id}: fontName missing (would invent family)`);
  }
  if (raw.fontSize === null || !(raw.fontSize > 0)) {
    throw new Error(`Text ${raw.id}: fontSize missing (would invent size)`);
  }
  return {
    fontFamily: raw.fontName,
    fontSize: raw.fontSize,
    fontWeight: raw.fontWeight === null ? 'normal' : raw.fontWeight,
    fontStyle: 'normal',
    // Empty string = provider did not capture color · renderer must not invent paint.
    color: raw.color === null ? '' : raw.color,
    textAlign: 'left',
  };
}

function extractTextElements(raw: RawPageData, unit: BBox['unit']): TextElement[] {
  return raw.textBlocks.map((block) => {
    if (block.text.length === 0) {
      throw new Error(`Text ${block.id}: empty content`);
    }
    const bbox: BBox = {
      x: block.x,
      y: block.y,
      width: block.width,
      height: block.height,
      unit,
    };
    validateBBox(bbox);
    return {
      id: block.id,
      type: 'text',
      bbox,
      layer: 10,
      content: block.text,
      style: mapTextStyle(block),
      ...(block.linePaint !== undefined ? { linePaint: block.linePaint } : {}),
    };
  });
}

function extractImageElements(raw: RawPageData, unit: BBox['unit']): ImageElement[] {
  return raw.imageBlocks.map((block) => {
    if (block.assetId.trim().length === 0) {
      throw new Error(`Image ${block.id}: assetId missing`);
    }
    const bbox: BBox = {
      x: block.x,
      y: block.y,
      width: block.width,
      height: block.height,
      unit,
    };
    validateBBox(bbox);
    return {
      id: block.id,
      type: 'image',
      bbox,
      layer: block.layer ?? 0,
      assetId: block.assetId,
      objectFit: block.objectFit ?? 'contain',
    };
  });
}

function assertAssetsCoverImages(
  images: readonly ImageElement[],
  assets: AssetLibrary,
): void {
  for (const image of images) {
    const asset = assets[image.assetId];
    if (!asset) {
      throw new Error(`Image ${image.id}: asset ${image.assetId} not in AssetLibrary`);
    }
    if (asset.src.trim().length === 0) {
      throw new Error(`Asset ${image.assetId}: empty src`);
    }
  }
}

function readingOrderIds(elements: readonly SPSUElement[]): readonly string[] {
  return [...elements]
    .sort((a, b) => {
      if (Math.abs(a.bbox.y - b.bbox.y) > 5) return a.bbox.y - b.bbox.y;
      return a.bbox.x - b.bbox.x;
    })
    .map((el) => el.id);
}

// =============================================================================
// 3. EXTRACT → SPSUPage
// =============================================================================

/** U-FID-02 observe-only midpoints · omitted in production (undefined). */
export type ExtractToSpsuObservePhase =
  | 'extractTextElements'
  | 'reconcileTextAndSoftmasks';

/**
 * Map measured raw page data into a strict SPSUPage.
 * Throws on invalid geometry, missing typography captures, or orphan asset ids.
 * Optional `observe` is read-only · never feeds back into the returned page.
 */
export function extractToSPSUPage(
  rawData: RawPageData,
  assets: AssetLibrary,
  observe?: (phase: ExtractToSpsuObservePhase, texts: readonly TextElement[]) => void,
  /** §438 R-2 · rekod plate Pass 3 yang belum terbukti · optional · pemanggil lama tak berubah. */
  onNotProven?: (imageId: string, verdict: string) => void,
): SPSUPage {
  if (!(rawData.pageWidth > 0) || !(rawData.pageHeight > 0)) {
    throw new Error('Invalid page dimensions');
  }
  if (!(rawData.pageNumber > 0)) {
    throw new Error('Invalid pageNumber');
  }

  const unit = rawData.unit ?? 'pt';
  const extractedTexts = extractTextElements(rawData, unit);
  observe?.('extractTextElements', extractedTexts);
  const extractedImages = extractImageElements(rawData, unit);
  const { images: imageElements, texts: textElements } = reconcileTextAndSoftmasks(
    extractedImages,
    extractedTexts,
    assets,
    onNotProven,
  );
  observe?.('reconcileTextAndSoftmasks', textElements);
  assertAssetsCoverImages(imageElements, assets);

  const allElements: SPSUElement[] = [...imageElements, ...textElements];

  return {
    id: `page-${rawData.pageNumber}`,
    pageNumber: rawData.pageNumber,
    size: {
      width: rawData.pageWidth,
      height: rawData.pageHeight,
      unit,
    },
    elements: allElements,
    readingOrder: readingOrderIds(allElements),
  };
}
